import { ViewPort } from './ViewPort';
import { Engine } from '../Engine';
import { Context } from './Context';
import { DOMTextManager, TextManager } from '../text';
import { ResourceLoader } from '../resource/ResourceLoader';
import { HttpLoadingStrategy } from '../resource/HttpLoadingStrategy';

export class DOMContextBuilder {
  public displayWidth: number;
  public displayHeight: number;

  constructor(
    public viewPort: ViewPort,
    public canvas: HTMLCanvasElement,
    public overlayLayer: HTMLDivElement,
    private assetBaseUrl: string = '',
  ) {
    this.displayWidth = viewPort.width;
    this.displayHeight = viewPort.height;
  }

  public resizeCanvas(width: number, height: number, renderScale: number = 1.0): void {
    this.displayWidth = width;
    this.displayHeight = height;
    const renderWidth = Math.max(1, Math.round(width * renderScale));
    const renderHeight = Math.max(1, Math.round(height * renderScale));
    this.viewPort.width = renderWidth;
    this.viewPort.height = renderHeight;
    this.viewPort.displayWidth = width;
    this.viewPort.displayHeight = height;
    this.canvas.width = renderWidth;
    this.canvas.height = renderHeight;
    this.canvas.style.width = width + 'px';
    this.canvas.style.height = height + 'px';
  }

  public createContext(engine: Engine): Context {
    const gl = this.canvas.getContext('webgl2', {
      preserveDrawingBuffer: true,
      stencil: true,
    });
    if (!gl) {
      throw new Error('WebGL2 not supported');
    }

    // The deferred renderer's G-buffer (and volumetric pass) allocate RGBA16F color
    // attachments. Without EXT_color_buffer_float those framebuffers are incomplete
    // and the engine cannot render. Fail loudly here instead of producing a black screen.
    if (!gl.getExtension('EXT_color_buffer_float')) {
      throw new Error(
        'WebGL2 extension EXT_color_buffer_float is required (deferred renderer uses RGBA16F render targets)',
      );
    }
    // EVSM moments target is RG32F so the variance signal (M2 - M1²) survives
    // smooth-terrain depth gradients — RG16F precision at the warped values
    // (~22000 for the squared moment) is below the per-frame variance and
    // produces visible isoline ripples on slow-varying surfaces.
    if (!gl.getExtension('OES_texture_float_linear')) {
      throw new Error(
        'WebGL2 extension OES_texture_float_linear is required (CSM EVSM moments use RG32F linear-filtered)',
      );
    }

    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.BLEND);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.clearColor(0.0, 0.0, 0.0, 1.0);
    gl.enable(gl.DEPTH_TEST);

    this.canvas.width = this.viewPort.width;
    this.canvas.height = this.viewPort.height;
    this.canvas.style.width = this.displayWidth + 'px';
    this.canvas.style.height = this.displayHeight + 'px';

    const resourceLoader = new ResourceLoader(new HttpLoadingStrategy(this.assetBaseUrl));
    return new Context(engine, gl, this.viewPort, resourceLoader);
  }

  public buildTextManager(_engine: Engine): TextManager {
    return new DOMTextManager(this.overlayLayer);
  }
}
